import { useDispatch, useSelector } from 'react-redux'

import { removeLocalList } from 'store/slices/localProductsList'

import { mindBoxSDK } from '@/shared/integrations/mindBox'
import { getAddToCart } from '@/shared/integrations/yandex-metric/data-layers/get-add-to-cart/get-add-to-cart'
import { getRemoveFromCart } from '@/shared/integrations/yandex-metric/data-layers/get-remove-from-cart/get-remove-from-cart'
import { pushToDataLayer } from '@/shared/integrations/yandex-metric/utils/pushToDataLayer'
import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { getPhoneFromJwtToken } from '@/shared/lib/utils/jwt/get-phone-from-jwt-token'
import { CatalogItemLocation, TAddProductRequest } from '@/shared/types/deprecated-types'

import {
	useAddCartItemMutation,
	useAddCartItemsMutation,
	useLazyClearCartQuery,
	useRemoveCartItemMutation,
	useUpdateCartItemQuantityMutation
} from '../api/cart-api-slice'
import { removedProductFromCartEvent } from '../model/events'
import { TCartItem } from '../types/cart-item'

export type DeleteItemOptions = {
	location?: `${CatalogItemLocation}`
	sectionName?: string
	updateBasket?: boolean
}

type CartActionsParams = {
	products: TCartItem[]
	refetch: () => Promise<any>
}

const cartQuantityUpdates = new Map<number, Promise<unknown>>()

export const useCartActions = ({ products, refetch }: CartActionsParams) => {
	const dispatch = useDispatch()

	const [removeItem] = useRemoveCartItemMutation()
	const [updateQuantity] = useUpdateCartItemQuantityMutation()
	const [clearCartMutation] = useLazyClearCartQuery()
	const [addCartItem] = useAddCartItemMutation()
	const [addCartItems] = useAddCartItemsMutation()

	const localProductsList = useSelector((state: any) => state.localProductsList?.items || {})
	const sessionToken = useSelector((state: any) => state.session?.token)

	const deleteItem = async (idBasket: number, productId?: number, options?: DeleteItemOptions) => {
		try {
			const { location, sectionName } = options || {}

			// Find cart item for Yandex Metrika before removal
			const cartItem = products?.find(item => item.ID_BASKET === idBasket)

			// Push Yandex Metrika event
			if (cartItem) {
				const payload = {
					ID: idBasket,
					KOD: String(idBasket),
					TITLE: cartItem.TITLE || '',
					PROPERTIES: cartItem.PROPERTIES || {},
					SECTION_ID: cartItem.SECTION_ID ? Number(cartItem.SECTION_ID) : 0
				}
				pushToDataLayer(getRemoveFromCart(payload, sectionName || '', location || ''))
			}

			await removeItem({ ID: idBasket }).unwrap()

			if (productId) {
				dispatch(removedProductFromCartEvent({ productId }))
			}
		} catch (error) {
			console.error('Failed to remove item:', error)
		}
	}

	const changeQuantity = async (idBasket: number, quantity: number, updateBasket?: boolean) => {
		const request = (cartQuantityUpdates.get(idBasket) ?? Promise.resolve())
			.catch(() => undefined)
			.then(() => updateQuantity({ ID: idBasket, quantity }).unwrap())
		cartQuantityUpdates.set(idBasket, request)

		try {
			await request

			// Send mindBox event after quantity update
			if (updateBasket) {
				const { data } = await refetch()
				if (data?.PRODUCTS) {
					const userPhone = getPhoneFromJwtToken(sessionToken)
					mindBoxSDK.sendCartUpdateEvent(data.PRODUCTS, userPhone)
				}
			}
		} catch (error) {
			console.error('Failed to update quantity:', error)
		} finally {
			if (cartQuantityUpdates.get(idBasket) === request) cartQuantityUpdates.delete(idBasket)
		}
	}

	const changeCount = (idBasket: number, quantity: number, productId?: number, options?: DeleteItemOptions) => {
		if (!quantity) {
			deleteItem(idBasket, productId, options)
		} else {
			changeQuantity(idBasket, quantity, options?.updateBasket)
		}
	}

	const clearCart = async () => {
		try {
			await clearCartMutation()

			mindBoxSDK.sendCartUpdateEvent([])
		} catch (error) {
			console.error('Failed to clear cart:', error)
		}
	}

	const addItem = async (productData: TAddProductRequest) => {
		try {
			const { ID, SECTION_ID, SECTION_NAME, location } = productData
			const localList = localProductsList[ID]
			const userPhone = getPhoneFromJwtToken(sessionToken)

			const metricPayload = { ...productData, SECTION_ID: Number(SECTION_ID) }
			pushToDataLayer(getAddToCart(metricPayload, SECTION_NAME, location))

			if (localList && localList.length) {
				const productList = [productData, ...localList]

				await addCartItems({ products: productList }).unwrap()
				sendYandexMetricaEvent('v-korzinu')

				dispatch(removeLocalList({ ID }))
			} else {
				const { data } = await addCartItem({
					productId: productData.ID,
					relatedProduct: productData.RELATED_PRODUCTS,
					typeProduct: productData.TYPE_PRODUCTS
				}).unwrap()
				sendYandexMetricaEvent('v-korzinu')

				if (data) {
					mindBoxSDK.sendCartUpdateEvent(data, userPhone)
				}
			}
		} catch (error) {
			console.error('Failed to add item to cart:', error)
		}
	}

	const getCartInfoWithEvents = async () => {
		try {
			const { data } = await refetch()
			const userPhone = getPhoneFromJwtToken(sessionToken)

			if (data?.PRODUCTS) {
				mindBoxSDK.sendCartUpdateEvent(data.PRODUCTS, userPhone)
			} else {
				mindBoxSDK.sendCartUpdateEvent([], userPhone)
			}

			return data
		} catch (error) {
			console.error('Failed to get cart info:', error)
		}
	}

	return {
		deleteItem,
		changeCount,
		changeQuantity,
		clearCart,
		addItem,
		updateCart: refetch,
		getCartInfoWithEvents
	}
}
